// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import VideoAsset, { VideoAssetsStyles } from "@conklin/App/Components/Media/VideoAsset";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

const enum fields {
    maxNumberOfVideos = "maxNumberOfVideos",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.maxNumberOfVideos]: number;
    };
}

export interface ProductDetailsVideosStyles {
    wrapper?: AccordionSectionPresentationProps;
    imageWrapper?: InjectableCss;
    videosContainer?: GridContainerProps;
    videoContainer?: GridItemProps;
    video?: VideoAssetsStyles;
}

export const productDetailsVideosStyles: ProductDetailsVideosStyles = {};

const styles = productDetailsVideosStyles;

type Props = OwnProps & HasProductContext;

const ProductDetailsVideos: FC<Props> = ({ productContext, fields: { maxNumberOfVideos = 3 } }) => {
    const videos = productContext?.product.documents?.filter(doc => doc.documentType.toLowerCase() === "video") ?? [];
    if (!videos.length) {
        return null;
    }
    return (
        <ManagedAccordionSection title={translate("Videos")} initialExpanded={true} {...styles.wrapper}>
            <StyledWrapper {...styles.videosContainer}>
                {videos.slice(0, maxNumberOfVideos).map(video => (
                        <StyledWrapper {...styles.imageWrapper} key={video.id}>
                            <VideoAsset
                                {...video}
                                type="Vimeo"
                                url={video.filePath}
                                isThumbnailClickable
                                showThumbnail={true}
                                extendedStyles={styles.video}
                            />
                        </StyledWrapper>
                ))}
            </StyledWrapper>
        </ManagedAccordionSection>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(ProductDetailsVideos),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.maxNumberOfVideos,
                displayName: "Max Number Of Videos",
                editorTemplate: "IntegerField",
                defaultValue: 3,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
